import React from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Mic, Check, ArrowLeft } from 'lucide-react';
import { toast } from 'react-toastify';
import { VoiceDetailsStep } from '../components/ai-voices/voice-cloning';
import { AudioInput, AudioInputFile } from '../components/audio-input';
import { useVoiceCloningStore } from '../zustand-store/useVoiceCloningStore';
import { useLanguages } from '../http-hooks/ai-voices/useLanguages';
import { useAIVoicesStore } from '../zustand-store/useAIVoicesStore';
import InstantVoiceCloneSkeleton from '../components/skeletons/pages/InstantVoiceCloneSkeleton';
import { useGetPresignedUrl, uploadToS3, useCreateClonedVoice } from '../http-hooks/ai-voices/useClonedVoice';
import FeatureCreditCost from '../components/plans/FeatureCreditCost';
import { useFeatureCreditGate } from '../http-hooks/organisation/useFeatureCreditEstimate';
import { Card, PageHeader, SectionLabel } from '../components/ui/bootstrap';
import { addMixPanelEvent } from '../store/actions';
import { ERROR_OCCURRED } from '../constants/mixpanel-events';


interface StepIndicatorProps {
    step: number;
    title: string;
    subtitle: string;
    isActive: boolean;
    isCompleted: boolean;
}

const StepIndicator: React.FC<StepIndicatorProps> = ({ step, title, subtitle, isActive, isCompleted }) => {
    return (
        <div className="d-flex align-items-start" style={{ gap: 14, marginBottom: 20 }}>
            <div style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: isCompleted
                    ? 'linear-gradient(135deg, var(--bs-secondary) 0%, var(--bs-primary) 100%)'
                    : isActive
                        ? 'linear-gradient(135deg, var(--bs-primary) 0%, var(--bs-secondary) 100%)'
                        : 'rgba(var(--bs-primary-rgb), 0.06)',
                border: isActive || isCompleted
                    ? 'none'
                    : '1px solid rgba(var(--bs-primary-rgb), 0.15)',
                color: isActive || isCompleted ? 'white' : 'var(--bs-secondary-color)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '700',
                fontSize: '12.5px',
                flexShrink: 0,
                boxShadow: isActive ? '0 4px 12px rgba(var(--bs-primary-rgb), 0.30)' : 'none',
                transition: 'all 0.3s ease',
            }}>
                {isCompleted ? <Check size={14} strokeWidth={3} /> : step}
            </div>
            <div>
                <div style={{
                    fontSize: '13px',
                    fontWeight: isActive ? '600' : '500',
                    color: isActive ? 'var(--bs-emphasis-color)' : isCompleted ? 'var(--bs-body-color)' : 'var(--bs-secondary-color)',
                    lineHeight: '1.3',
                    marginBottom: 2
                }}>
                    {title}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--bs-secondary-color)', lineHeight: '1.4' }}>{subtitle}</div>
            </div>
        </div>
    );
};

export default function InstantVoiceClone() {
    const { org_id, id } = useParams<{ org_id: string; id: string }>();
    const history = useHistory();
    const dispatch = useDispatch();

    const setLanguages = useAIVoicesStore(state => state.setLanguages);
    const { isLoading } = useLanguages((data) => setLanguages(data));

    const {
        currentStep,
        setCurrentStep,
        audioFile,
        audioUrl,
        audioDuration,
        voiceDetails,
        uploadedAudioUrl,
        setAudioFile,
        setUploadedAudioUrl,
        isStep1Valid,
        isStep2Valid,
        resetStore
    } = useVoiceCloningStore();

    const audioValue: AudioInputFile | null = audioFile
        ? {
            file: audioFile,
            previewUrl: audioUrl ?? '',
            // 0 means "not measured yet" — map back to undefined so the probe re-runs.
            durationSec: audioDuration || undefined,
        }
        : null;

    const handleAudioChange = (value: AudioInputFile | AudioInputFile[] | null) => {
        const next = Array.isArray(value) ? value[0] ?? null : value;
        if (next) {
            setAudioFile(next.file, next.previewUrl, next.durationSec ?? 0);
        } else {
            setAudioFile(null, null, 0);
        }
    };

    React.useEffect(() => {
        resetStore();
    }, [resetStore]);

    const getPresignedUrlMutation = useGetPresignedUrl();
    const createClonedVoiceMutation = useCreateClonedVoice();
    const isUploading = createClonedVoiceMutation.isPending;
    const [isUploadingAudio, setIsUploadingAudio] = React.useState(false);

    const { insufficientCredits: hasInsufficientCredits } = useFeatureCreditGate({
        org_id, feature: 'voice-clone', enabled: currentStep === 2,
    });

    const handleContinueToStep2 = async () => {
        if (!isStep1Valid() || !audioFile || !org_id || !id) return;
        try {
            if (uploadedAudioUrl) { setCurrentStep(2); return; }
            setIsUploadingAudio(true);
            const presignedData = await getPresignedUrlMutation.mutateAsync({
                org_id, integration_id: id, fileName: audioFile.name, contentType: audioFile.type
            });
            await uploadToS3(presignedData.signed_url, audioFile);
            const audioSampleUrl = `${process.env.REACT_APP_CLONED_VOICE_BUCKET_URL || process.env.REACT_APP_AUDIO_PREVIEW_URL}/${presignedData.url}`;
            setUploadedAudioUrl(audioSampleUrl);
            toast.success('Audio uploaded successfully!');
            setCurrentStep(2);
        } catch (error: any) {
            console.error('Upload error:', error);
            toast.error(error.message || 'Failed to upload audio file');
            dispatch(addMixPanelEvent({
                orgId: org_id, integrationId: id, body: {
                    eventName: ERROR_OCCURRED,
                    eventData: {
                        error_type: 'upload_failed',
                        feature_name: 'voice_clone',
                        page: window.location.pathname,
                    },
                },
            }));
        } finally {
            setIsUploadingAudio(false);
        }
    };

    const handleSaveClonedVoice = async () => {
        if (!uploadedAudioUrl || !org_id || !id) {
            toast.error('Audio file not uploaded. Please go back to Step 1.');
            return;
        }
        try {
            const url = new URL(uploadedAudioUrl);
            const audioSamplePath = url.pathname.substring(1);
            dispatch(addMixPanelEvent({
                orgId: org_id, integrationId: id, body: {
                    eventName: "VOICE_CLONE_STARTED",
                    eventData: {
                        tool: "voice_clone", // GROW-84: tool on every stage for start->finish comparison
                        voice_name: voiceDetails.name,
                        language_code: voiceDetails.languageCode,
                        description: voiceDetails.description,
                        audio_duration: audioDuration,
                        audio_sample_path: audioSamplePath,
                    }
                }
            }));
            await createClonedVoiceMutation.mutateAsync({
                org_id, integration_id: id, voice_name: voiceDetails.name,
                language_code: voiceDetails.languageCode, description: voiceDetails.description,
                audio_sample_path: audioSamplePath, audio_duration: audioDuration
            });
            resetStore();
            toast.success('Your voice is being cloned. We\'ll let you know when it\'s ready.');
            history.push(`/organisation/${org_id}/integration/${id}/voice?tab=my-voices`);
        } catch (error) {
            console.error('Error creating cloned voice:', error);
            toast.error('Failed to clone voice. Please try again.');
            dispatch(addMixPanelEvent({
                orgId: org_id, integrationId: id, body: {
                    eventName: ERROR_OCCURRED,
                    eventData: {
                        error_type: 'request_failed',
                        feature_name: 'voice_clone',
                        page: window.location.pathname,
                    },
                },
            }));
        }
    };

    return (
        <div>
            {/* Page Header */}
            <PageHeader
                title="Clone Your Voice"
                subtitle="Just 10–30 seconds of audio. Studio-quality voice cloning."
                left={
                    <button
                        onClick={() => history.push(`/organisation/${org_id}/integration/${id}/voice`)}
                        style={{
                            background: 'rgba(var(--bs-primary-rgb), 0.06)',
                            border: '1px solid rgba(var(--bs-primary-rgb), 0.14)',
                            borderRadius: 10,
                            width: 36,
                            height: 36,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            color: 'var(--bs-secondary-color)',
                            flexShrink: 0,
                            transition: 'all 0.18s ease',
                            padding: 0,
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--bs-primary)'; e.currentTarget.style.borderColor = 'rgba(var(--bs-primary-rgb), 0.30)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--bs-secondary-color)'; e.currentTarget.style.borderColor = 'rgba(var(--bs-primary-rgb), 0.14)'; }}
                    >
                        <ArrowLeft size={16} />
                    </button>
                }
            />

            {isLoading ? (
                <InstantVoiceCloneSkeleton />
            ) : (
                <div className="row g-4">
                    {/* Left Sidebar — Steps */}
                    <div className="col-12 col-lg-3">
                        <Card className="p-4">
                            <SectionLabel className="mb-4">Progress</SectionLabel>
                            <StepIndicator
                                step={1}
                                title="Upload Audio"
                                subtitle="Provide a voice sample"
                                isActive={currentStep === 1}
                                isCompleted={currentStep > 1}
                            />
                            {/* Connector line */}
                            <div style={{ width: 1, height: 18, background: 'rgba(var(--bs-primary-rgb), 0.12)', marginLeft: 14, marginBottom: 4, marginTop: -10, borderRadius: 2 }} />
                            <StepIndicator
                                step={2}
                                title="Voice Details"
                                subtitle="Name and save your voice"
                                isActive={currentStep === 2}
                                isCompleted={false}
                            />
                        </Card>
                    </div>

                    {/* Main Content */}
                    <div className="col-12 col-lg-9">
                        <Card className="p-4">

                            {/* Step 1 */}
                            {currentStep === 1 && (
                                <>
                                    {/* Hero callout */}
                                    <div className="mb-4 p-4" style={{
                                        background: 'linear-gradient(135deg, rgba(var(--bs-primary-rgb), 0.08) 0%, rgba(var(--bs-secondary-rgb, 96,181,255), 0.06) 100%)',
                                        border: '1px solid rgba(var(--bs-primary-rgb), 0.16)',
                                        borderRadius: '14px',
                                    }}>
                                        <div className="d-flex align-items-center gap-3">
                                            <div style={{
                                                width: 44, height: 44, borderRadius: '50%',
                                                background: 'linear-gradient(135deg, var(--bs-primary) 0%, var(--bs-secondary) 100%)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                                boxShadow: '0 4px 12px rgba(var(--bs-primary-rgb), 0.25)',
                                            }}>
                                                <Mic size={20} color="white" />
                                            </div>
                                            <div>
                                                <h6 style={{ fontSize: '15px', fontWeight: 700, color: 'var(--bs-emphasis-color)', margin: 0, lineHeight: 1.3 }}>
                                                    Clone your voice with just 10–30 seconds of audio
                                                </h6>
                                                <p style={{ fontSize: '13px', color: 'var(--bs-secondary-color)', margin: 0, lineHeight: 1.5, marginTop: 2 }}>
                                                    Studio-quality results — record or upload a short clip, and we'll create a natural-sounding digital voice.
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Tips banner */}
                                    <div className="mb-4 p-3" style={{
                                        background: 'rgba(var(--bs-primary-rgb), 0.05)',
                                        border: '1px solid rgba(var(--bs-primary-rgb), 0.14)',
                                        borderRadius: '12px',
                                    }}>
                                        <div className="d-flex align-items-center gap-2 mb-3">
                                            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M10 6V10M10 14H10.01M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z" stroke="var(--bs-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                            <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--bs-primary)' }}>Best Practices for Quality Cloning</span>
                                        </div>
                                        <div className="row g-2">
                                            {[
                                                'Record in a quiet space with minimal background noise',
                                                'Use a quality microphone or headset for clearer audio',
                                                'Maintain consistent audio levels throughout',
                                                'Speak naturally and clearly at your normal pace',
                                            ].map((tip, i) => (
                                                <div key={i} className="col-12 col-md-6">
                                                    <div className="d-flex align-items-start gap-2">
                                                        <span style={{ color: 'var(--bs-secondary)', fontSize: '14px', lineHeight: 1.4, flexShrink: 0 }}>✓</span>
                                                        <p style={{ fontSize: '12px', color: 'var(--bs-body-color)', marginBottom: 0, lineHeight: '1.6' }}>{tip}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Upload / Recording area — preview is rendered internally by AudioInput */}
                                    <AudioInput
                                        value={audioValue}
                                        onChange={handleAudioChange}
                                        onError={(e) => toast.error(e.message)}
                                        validation={{ maxSizeBytes: 15 * 1024 * 1024, allowedMimePrefixes: ['audio/', 'video/'], minDurationSec: 10, maxDurationSec: 30 }}
                                        recording={{ micDeviceSelector: true, maxRecordingDurationSec: 30 }}
                                    />

                                    {/* Footer row */}
                                    <div className="mt-4 d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3">
                                        <div className="d-flex align-items-center gap-2">
                                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                                                <path d="M8 5.33333V8M8 10.6667H8.00667M14.6667 8C14.6667 11.6819 11.6819 14.6667 8 14.6667C4.31809 14.6667 1.33333 11.6819 1.33333 8C1.33333 4.31809 4.31809 1.33333 8 1.33333C11.6819 1.33333 14.6667 4.31809 14.6667 8Z" stroke="var(--bs-secondary-color)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                            <span style={{ fontSize: '12px', color: 'var(--bs-secondary-color)' }}>
                                                Minimum 10 seconds of clear audio required
                                            </span>
                                        </div>
                                        <button
                                            className="btn btn-sm fw-semibold"
                                            disabled={!isStep1Valid() || isUploadingAudio}
                                            title={audioDuration > 0 && audioDuration < 10 ? `Audio is ${Math.floor(audioDuration)}s. Minimum 10s required.` : ''}
                                            onClick={handleContinueToStep2}
                                            style={{
                                                background: isStep1Valid() && !isUploadingAudio
                                                    ? 'linear-gradient(135deg, var(--bs-primary) 0%, var(--bs-secondary) 100%)'
                                                    : 'rgba(var(--bs-primary-rgb), 0.10)',
                                                color: isStep1Valid() && !isUploadingAudio ? 'white' : 'var(--bs-secondary-color)',
                                                border: 'none',
                                                borderRadius: '10px',
                                                padding: '9px 22px',
                                                fontSize: '13px',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 6,
                                                boxShadow: isStep1Valid() && !isUploadingAudio ? '0 4px 14px rgba(var(--bs-primary-rgb), 0.25)' : 'none',
                                                transition: 'all 0.2s ease',
                                            }}
                                        >
                                            {isUploadingAudio ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                    Uploading...
                                                </>
                                            ) : 'Continue →'}
                                        </button>
                                    </div>
                                </>
                            )}

                            {/* Step 2 */}
                            {currentStep === 2 && (
                                <>
                                    <div className="mb-4">
                                    <h5 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--bs-emphasis-color)', marginBottom: 4 }}>Voice Details</h5>
                                    <p style={{ fontSize: '13px', color: 'var(--bs-secondary-color)', marginBottom: 0 }}>
                                            Add information about your cloned voice
                                        </p>
                                    </div>

                                    <VoiceDetailsStep />

                                    <div className="mt-4 d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center justify-content-between gap-3">
                                        <button
                                            onClick={() => setCurrentStep(1)}
                                            disabled={isUploading}
                                            style={{
                                                background: 'transparent',
                                                border: '1px solid rgba(var(--bs-primary-rgb), 0.20)',
                                                color: 'var(--bs-secondary-color)',
                                                borderRadius: '10px',
                                                padding: '9px 20px',
                                                fontSize: '13px',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 6,
                                                cursor: 'pointer',
                                                transition: 'all 0.18s ease',
                                            }}
                                            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(var(--bs-primary-rgb), 0.40)'; e.currentTarget.style.color = 'var(--bs-body-color)'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(var(--bs-primary-rgb), 0.20)'; e.currentTarget.style.color = 'var(--bs-secondary-color)'; }}
                                        >
                                            <ArrowLeft size={14} /> Back
                                        </button>
                                        <div className="d-flex align-items-center gap-3">
                                        <FeatureCreditCost orgId={org_id} feature="voice-clone" />
                                        <button
                                            disabled={!isStep2Valid() || isUploading || hasInsufficientCredits}
                                            title={!isStep2Valid() ? 'Please fill in all required fields and accept the terms' : hasInsufficientCredits ? 'Not enough credits to clone a voice' : ''}
                                            onClick={handleSaveClonedVoice}
                                            style={{
                                                background: isStep2Valid() && !hasInsufficientCredits
                                                    ? 'linear-gradient(135deg, var(--bs-primary) 0%, var(--bs-secondary) 100%)'
                                                    : 'rgba(var(--bs-primary-rgb), 0.10)',
                                                color: isStep2Valid() && !hasInsufficientCredits ? 'white' : 'var(--bs-secondary-color)',
                                                border: 'none',
                                                borderRadius: '10px',
                                                padding: '9px 24px',
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 6,
                                                cursor: isStep2Valid() && !hasInsufficientCredits ? 'pointer' : 'not-allowed',
                                                boxShadow: isStep2Valid() && !hasInsufficientCredits ? '0 4px 14px rgba(var(--bs-primary-rgb), 0.25)' : 'none',
                                                transition: 'all 0.2s ease',
                                            }}
                                        >
                                            {isUploading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                    Saving...
                                                </>
                                            ) : 'Save Voice'}
                                        </button>
                                        </div>
                                    </div>
                                </>
                            )}

                        </Card>
                    </div>
                </div>
            )}
        </div>
    );
}
